import { Col, Row, Form, Input } from 'antd';
import { useCallback } from 'react';
import debounce from 'lodash.debounce';
import { useTranslation } from 'react-i18next';
import { FaStarOfLife } from 'react-icons/fa6';
import { useLazyGetSubAccountListQuery } from '@src/services/subAccount/subAccountList/subAccountListApiSlice';
import { MultiSelect } from '@src/components';
import { INPUT_CONFIG } from '@src/constants/inputConfig';

type GetUsersLazyTrigger = ReturnType<typeof useLazyGetSubAccountListQuery>[0];

interface InputSectionProps {
    form: any;
    isFetching: boolean;
    selectedAccounts: string[];
    setSelectedAccounts: (
        value: string[],
        option?:
            | Record<string, string | number>
            | Record<string, string | number>[]
    ) => void;
    userOptions: Record<string, string | number>[];
    getUsers: GetUsersLazyTrigger;
    fetchUsers: (searchValue?: string) => Promise<void>;
}

export default function InputSection({
    form,
    fetchUsers,
    getUsers,
    userOptions,
    isFetching,
    selectedAccounts,
    setSelectedAccounts,
}: InputSectionProps) {
    const { t } = useTranslation();

    const debounceFetcher = useCallback(debounce(fetchUsers, 500), [getUsers]);

    return (
        <div className="flex flex-col">
            <Row gutter={24} className="!mb-0">
                <Col xs={24} sm={24} md={12} lg={12}>
                    <Form.Item
                        label={
                            <div className="flex items-center gap-1">
                                {t('pages.role.form.roleName')}
                                <FaStarOfLife color="red" size={8} />
                            </div>
                        }
                        name="roleName"
                        rules={[
                            {
                                required: true,
                                message: t('pages.role.form.roleNameRequired'),
                            },
                        ]}
                    >
                        <Input
                            size="large"
                            placeholder={t(
                                'pages.role.form.roleNamePlaceholder'
                            )}
                            maxLength={INPUT_CONFIG.SHORT_TEXT.maxLength}
                        />
                    </Form.Item>
                </Col>

                <Col xs={24} sm={24} md={12} lg={12}>
                    <Form.Item
                        label={
                            <div className="flex items-center gap-1">
                                {t('pages.role.form.eligibleAccounts')}
                                <FaStarOfLife color="red" size={8} />
                            </div>
                        }
                        name="eligibleAccounts"
                        rules={[
                            {
                                required: true,
                                message: t(
                                    'pages.role.form.eligibleAccountsRequired'
                                ),
                            },
                        ]}
                    >
                        <MultiSelect
                            field={{
                                value: selectedAccounts.map((id) => {
                                    const option = userOptions.find(
                                        (o) => o.value === id || o.id === id
                                    );

                                    return {
                                        value: id.toString(),
                                        label: (
                                            option?.name ??
                                            option?.label ??
                                            ''
                                        ).toString(),
                                    };
                                }),
                                onChange: (val) => {
                                    const ids = val.map((v) => v.value);
                                    setSelectedAccounts(ids);
                                    form.setFieldsValue({
                                        eligibleAccounts: ids,
                                    });
                                    form.setFields([
                                        {
                                            name: 'eligibleAccounts',
                                            touched: true,
                                        },
                                    ]);
                                },
                            }}
                            options={userOptions.map((u) => ({
                                label:
                                    u.name?.toString() ??
                                    u.label?.toString() ??
                                    '',
                                value:
                                    u.id?.toString() ??
                                    u.value?.toString() ??
                                    '',
                            }))}
                            hasMore={false}
                            loadMore={() => {}}
                            loading={isFetching}
                            placeholder={t(
                                'pages.role.form.eligibleAccountsPlaceholder'
                            )}
                        />
                    </Form.Item>
                </Col>
            </Row>

            <Form.Item
                className="!mt-0"
                label={t('pages.role.form.roleDescription')}
                name="roleDescription"
            >
                <Input
                    size="large"
                    placeholder={t(
                        'pages.role.form.roleDescriptionPlaceholder'
                    )}
                    maxLength={200}
                    showCount
                    className="resize-none"
                />
            </Form.Item>
        </div>
    );
}
